import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Send } from "lucide-react";
import { brokerApi } from "@/hooks/useBrokerApi";

const DISTRIBUTION_OPTIONS = [
  { value: "one_driver", label: "One Driver" },
  { value: "multiple_drivers", label: "Multiple Drivers" },
  { value: "driver_group", label: "Driver Group" },
  { value: "eligible_network", label: "Eligible Network" },
];

const DriverPicker = ({ distributionType, network, selectedDriverIds, onSelectOne, onToggle }) => (
  <div className="max-h-56 overflow-y-auto border border-rr-line rounded-lg divide-y divide-rr-line">
    {network.length === 0 && <p className="text-xs text-rr-muted p-3">No connected drivers yet — add some in My Drivers.</p>}
    {network.map((c) => (
      <label key={c.id} className="flex items-center gap-2.5 px-3 py-2.5 text-sm cursor-pointer">
        <input
          type={distributionType === "one_driver" ? "radio" : "checkbox"}
          name="send-opportunity-driver"
          checked={selectedDriverIds.includes(c.driver_id)}
          onChange={() => (distributionType === "one_driver" ? onSelectOne(c.driver_id) : onToggle(c.driver_id))}
          data-testid={`send-opportunity-driver-${c.driver_id}`}
        />
        {c.invited_name}
      </label>
    ))}
  </div>
);

const TargetPicker = ({ distributionType, network, groups, selectedDriverIds, selectedGroupId, onSelectOne, onToggleDriver, onSelectGroup }) => {
  if (["one_driver", "multiple_drivers"].includes(distributionType)) {
    return <DriverPicker distributionType={distributionType} network={network} selectedDriverIds={selectedDriverIds} onSelectOne={onSelectOne} onToggle={onToggleDriver} />;
  }
  if (distributionType === "driver_group") {
    return (
      <select required data-testid="send-opportunity-group-select" value={selectedGroupId} onChange={(e) => onSelectGroup(e.target.value)} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm bg-white focus:outline-none focus:border-rr-orange">
        <option value="">Select a group</option>
        {groups.map((g) => (<option key={g.id} value={g.id}>{g.name} ({g.driver_ids.length})</option>))}
      </select>
    );
  }
  return <p className="text-xs text-rr-muted">This will notify every connected driver in your network.</p>;
};

export const BrokerSendOpportunityModal = ({ load, onOpenChange, onSent }) => {
  const [distributionType, setDistributionType] = useState("one_driver");
  const [network, setNetwork] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!load) return;
    brokerApi.get("/network").then(({ data }) => setNetwork(data.filter((c) => c.driver_id))).catch(() => setNetwork([]));
    brokerApi.get("/groups").then(({ data }) => setGroups(data)).catch(() => setGroups([]));
    setSelectedDriverIds([]);
    setSelectedGroupId("");
    setDistributionType("one_driver");
  }, [load]);

  const toggleDriver = (id) => setSelectedDriverIds((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await brokerApi.post(`/loads/${load.id}/send`, {
        distribution_type: distributionType,
        target_driver_ids: ["one_driver", "multiple_drivers"].includes(distributionType) ? selectedDriverIds : [],
        target_group_id: distributionType === "driver_group" ? selectedGroupId : null,
      });
      toast.success(`Opportunity sent to ${data.drivers_notified} driver${data.drivers_notified === 1 ? "" : "s"}.`);
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send this opportunity.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!load} onOpenChange={onOpenChange}>
      <DialogContent data-testid="broker-send-opportunity-modal" className="max-w-md rounded-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-rr-navy">Who Should Receive This Opportunity?</DialogTitle>
          <DialogDescription>{load?.origin} → {load?.destination}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} data-testid="broker-send-opportunity-form" className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {DISTRIBUTION_OPTIONS.map((o) => (
              <button type="button" key={o.value} data-testid={`send-opportunity-distribution-${o.value}`} onClick={() => setDistributionType(o.value)}
                className={`text-xs font-bold px-3 py-1.5 rounded-full border ${distributionType === o.value ? "bg-rr-navy text-white border-rr-navy" : "border-rr-line text-rr-muted"}`}>
                {o.label}
              </button>
            ))}
          </div>

          <TargetPicker
            distributionType={distributionType}
            network={network}
            groups={groups}
            selectedDriverIds={selectedDriverIds}
            selectedGroupId={selectedGroupId}
            onSelectOne={(id) => setSelectedDriverIds([id])}
            onToggleDriver={toggleDriver}
            onSelectGroup={setSelectedGroupId}
          />

          <button type="submit" disabled={submitting} data-testid="broker-send-opportunity-submit-button" className="w-full bg-rr-orange text-white font-bold py-3 rounded-full hover:bg-rr-navy transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Send Opportunity <Send size={15} /></>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
